import type { ImgHTMLAttributes } from 'react';

/** Responsive widths matching the build script breakpoints */
const RESPONSIVE_WIDTHS = [640, 960, 1280, 1920];

interface OptimizedImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  /** Original image path, e.g. "/assets/DAK-R_01.png" or "/profile.jpg" */
  src: string;
  alt: string;
  /** If true, uses the -thumb.webp variant for WebP source */
  thumbnail?: boolean;
  /** Custom sizes attribute for responsive images (defaults to 100vw) */
  sizes?: string;
}

/**
 * Renders a `<picture>` element that lets the browser pick the best available
 * format (AVIF / WebP) and the right size for the viewport.
 *
 * Expects the build script (scripts/optimize-images.mjs) to have generated
 * responsive width variants alongside the original file.
 */
export default function OptimizedImage({
  src,
  alt,
  thumbnail = false,
  sizes = '100vw',
  ...imgProps
}: OptimizedImageProps) {
  const isRaster = /\.(png|jpe?g)$/i.test(src);

  if (!isRaster) {
    return <img src={src} alt={alt} {...imgProps} />;
  }

  const basePath = src.replace(/\.(png|jpe?g)$/i, '');

  if (thumbnail) {
    return (
      <picture className='w-auto h-full'>
        <source srcSet={`${basePath}-thumb.webp`} type="image/webp" className={imgProps.className} />
        <img src={src} alt={alt} {...imgProps} />
      </picture>
    );
  }

  const avifSrcSet = RESPONSIVE_WIDTHS
    .map((w) => `${basePath}-${w}w.avif ${w}w`)
    .join(', ');

  const webpSrcSet = RESPONSIVE_WIDTHS
    .map((w) => `${basePath}-${w}w.webp ${w}w`)
    .join(', ');

  return (
    <picture className='w-auto h-full'>
      <source srcSet={avifSrcSet} sizes={sizes} type="image/avif" />
      <source srcSet={webpSrcSet} sizes={sizes} type="image/webp" />
      <img src={src} alt={alt} {...imgProps} />
    </picture>
  );
}
